/** Google Ads (AW) + GA4 measurement via gtag loaded in public/index.html */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const GOOGLE_ADS_ID = 'AW-18303981511';
export const GA4_MEASUREMENT_ID = 'G-2LY90PCCH3';

const CHECKOUT_TOTAL_KEY = 'd88_checkout_total_cents';
const PURCHASE_TRACKED_KEY = 'd88_gads_purchase_tracked';

function gtag(...args: unknown[]) {
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

/** Persist order total before Stripe redirect so success page can attribute revenue. */
export function stashCheckoutTotalCents(totalCents: number) {
  try {
    sessionStorage.setItem(CHECKOUT_TOTAL_KEY, String(Math.max(0, Math.round(totalCents))));
  } catch {
    /* private mode / quota */
  }
}

export function readStashedCheckoutTotalCents(): number {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_TOTAL_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
  } catch {
    return 0;
  }
}

export function clearStashedCheckoutTotalCents() {
  try {
    sessionStorage.removeItem(CHECKOUT_TOTAL_KEY);
  } catch {
    /* ignore */
  }
}

/** SPA route changes — gtag only auto-tracks the first HTML load. */
export function trackGoogleAdsPageView(pagePath: string) {
  gtag('config', GOOGLE_ADS_ID, { page_path: pagePath });
  gtag('config', GA4_MEASUREMENT_ID, { page_path: pagePath });
  gtag('event', 'page_view', { page_path: pagePath });
}

export function trackGoogleAdsSignUp(method = 'email') {
  gtag('event', 'sign_up', { method });
  const sendTo = process.env.REACT_APP_GOOGLE_ADS_SIGNUP_CONVERSION?.trim();
  if (sendTo) {
    gtag('event', 'conversion', { send_to: sendTo });
  }
}

export function trackGoogleAdsPurchase(opts: {
  transactionId: string;
  valueCents: number;
  currency?: string;
}) {
  const transactionId = opts.transactionId.trim();
  if (!transactionId) return;

  try {
    const prior = sessionStorage.getItem(PURCHASE_TRACKED_KEY);
    if (prior === transactionId) return;
    sessionStorage.setItem(PURCHASE_TRACKED_KEY, transactionId);
  } catch {
    /* still attempt tracking */
  }

  const value = Math.max(0, opts.valueCents) / 100;
  const currency = opts.currency ?? 'USD';

  gtag('event', 'purchase', {
    transaction_id: transactionId,
    value,
    currency,
  });

  const sendTo = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_CONVERSION?.trim();
  if (sendTo) {
    gtag('event', 'conversion', {
      send_to: sendTo,
      transaction_id: transactionId,
      value,
      currency,
    });
  }

  clearStashedCheckoutTotalCents();
}
